import React from 'react';
import PropTypes from 'prop-types';
import { LuFolderSearch, LuInfo } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import { useTranslation } from '../../i18n/LanguageContext';
import { formatDate } from './projectHelpers';
import { HOURLY } from './useEmployeeProjects';

const truncate = (text, limit = 48) => {
  if (!text) return '-';
  return text.length > limit ? `${text.substring(0, limit)}…` : text;
};

const headClass = 'tw-whitespace-nowrap tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 dark:tw-text-slate-400';
const cellClass = 'tw-px-4 tw-py-3 tw-text-slate-700 dark:tw-text-slate-200';

/** The project rows of the "Projeler" dialog: cards on phones, a table from md up. */
const ProjectsList = ({ rows, loading, emptyMessage, isAdmin, onOpenDetail }) => {
  const { t, locale } = useTranslation();

  if (loading) {
    return (
      <ul aria-label={t('projects.loading')} className="tw-space-y-2">
        {[0, 1, 2].map((item) => (
          <li key={item} className="tw-h-16 tw-animate-pulse tw-rounded-2xl tw-bg-slate-100 dark:tw-bg-white/[0.05]" />
        ))}
      </ul>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-py-12 tw-text-center">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          <LuFolderSearch aria-hidden="true" className="tw-h-7 tw-w-7" />
        </span>
        <p className="tw-max-w-sm tw-text-sm tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{emptyMessage}</p>
      </div>
    );
  }

  const assignmentBadge = (status) => (status
    ? <Badge tone={status === 'active' ? 'success' : 'neutral'}>{status === 'active' ? t('common.active') : t('common.passive')}</Badge>
    : <span className="tw-text-xs tw-text-slate-400">{t('projects.unassigned')}</span>);
  // projectStatus is stored in Turkish ("Aktif" / "Pasif")
  const projectBadge = (status) => (
    <Badge tone={status === 'Aktif' ? 'success' : 'neutral'}>{status === 'Aktif' ? t('common.active') : t('common.passive')}</Badge>
  );
  const fte = (value) => (value === HOURLY ? t('projects.hourly') : value);
  const detailButton = (row) => (
    <Button size="sm" onClick={() => onOpenDetail(row)}>
      <LuInfo aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      {t('projects.detail')}
    </Button>
  );

  return (
    <>
      <ul className="tw-space-y-2 md:tw-hidden">
        {rows.map((row) => (
          <li
            key={row.project.id}
            className={`tw-rounded-2xl tw-border tw-border-slate-200/80 tw-p-4 dark:tw-border-white/10 ${row.assignmentStatus === 'passive' ? 'tw-opacity-70' : ''}`}
          >
            <div className="tw-flex tw-items-start tw-justify-between tw-gap-3">
              <div className="tw-min-w-0">
                <p className="tw-truncate tw-font-mono tw-text-xs tw-font-bold tw-text-slate-500 dark:tw-text-slate-400">{row.project.titleShort || '-'}</p>
                <p className="tw-mt-1 tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{row.project.titleLong || '-'}</p>
              </div>
              {detailButton(row)}
            </div>
            <div className="tw-mt-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
              {assignmentBadge(row.assignmentStatus)}
              {projectBadge(row.projectStatus)}
              <span>{row.project.protocolNumber || '-'}</span>
              <span>· {formatDate(row.project.projectDate, locale)}</span>
              {isAdmin && <span>· {t('projects.columns.fte')}: {fte(row.fteValue)}</span>}
            </div>
          </li>
        ))}
      </ul>

      <div className="tw-hidden tw-overflow-x-auto tw-rounded-2xl tw-border tw-border-slate-200/80 glass-scrollbar md:tw-block dark:tw-border-white/10">
        <table className="tw-w-full tw-border-collapse tw-text-sm">
          <thead className="tw-bg-slate-50 dark:tw-bg-white/[0.04]">
            <tr className="tw-bg-transparent">
              <th className={headClass}>{t('projects.columns.code')}</th>
              <th className={headClass}>{t('projects.columns.protocol')}</th>
              <th className={headClass}>{t('projects.columns.date')}</th>
              <th className={headClass}>{t('projects.columns.name')}</th>
              <th className={headClass}>{t('projects.columns.assignment')}</th>
              <th className={headClass}>{t('projects.columns.status')}</th>
              {isAdmin && <th className={headClass}>{t('projects.columns.fte')}</th>}
              <th className={`${headClass} tw-text-right`}>{t('projects.columns.action')}</th>
            </tr>
          </thead>
          <tbody className="tw-divide-y tw-divide-slate-200/80 dark:tw-divide-white/10">
            {rows.map((row) => (
              <tr
                key={row.project.id}
                className={`tw-bg-transparent tw-transition-colors hover:tw-bg-slate-50/70 dark:hover:tw-bg-white/[0.03] ${row.assignmentStatus === 'passive' ? 'tw-opacity-70' : ''}`}
              >
                <td className={`${cellClass} tw-whitespace-nowrap tw-font-mono tw-text-xs tw-font-bold`}>{row.project.titleShort || '-'}</td>
                <td className={`${cellClass} tw-whitespace-nowrap`}>{row.project.protocolNumber || '-'}</td>
                <td className={`${cellClass} tw-whitespace-nowrap`}>{formatDate(row.project.projectDate, locale)}</td>
                <td className={cellClass} title={row.project.titleLong}>{truncate(row.project.titleLong)}</td>
                <td className={cellClass}>{assignmentBadge(row.assignmentStatus)}</td>
                <td className={cellClass}>{projectBadge(row.projectStatus)}</td>
                {isAdmin && <td className={`${cellClass} tw-tabular-nums`}>{fte(row.fteValue)}</td>}
                <td className={`${cellClass} tw-text-right`}>{detailButton(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

ProjectsList.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  emptyMessage: PropTypes.string,
  isAdmin: PropTypes.bool,
  onOpenDetail: PropTypes.func.isRequired,
};

export default ProjectsList;
